import { Controller } from "@hotwired/stimulus";

// Clones a <template> row for has_many nested_attributes and toggles _destroy
// on removal. Markup:
//   <div data-controller="nested-form" data-nested-form-max-value="8">
//     <div data-nested-form-target="list"> ...existing rows... </div>
//     <template data-nested-form-target="template"> ...row with NEW_RECORD... </template>
//     <button data-action="nested-form#add">Add</button>
//   </div>
// Each removable row: data-nested-form-target="item" with a hidden _destroy input
// and a button with data-action="nested-form#remove".
//
// `max` is optional. The server rejects a step that posts more rows than its
// cap, so when a max is given the Add button is disabled at the limit and a
// note says why, rather than letting someone fill in a row that cannot be saved.
export default class extends Controller {
  static targets = ["list", "template"];
  static values = { max: Number };

  connect() {
    this.refreshLimit();
  }

  add(event) {
    event.preventDefault();
    if (this.atLimit()) return;

    const html = this.templateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      new Date().getTime().toString(),
    );
    this.listTarget.insertAdjacentHTML("beforeend", html);
    this.refreshLimit();
  }

  remove(event) {
    event.preventDefault();
    const item = event.target.closest("[data-nested-form-target='item']");
    if (!item) return;

    const destroyField = item.querySelector("input[name*='_destroy']");
    if (destroyField) {
      destroyField.value = "1";
      item.hidden = true;
    } else {
      item.remove();
    }
    this.refreshLimit();
  }

  // Rows the applicant still means to keep. A removed persisted row stays in
  // the DOM, hidden and flagged _destroy; the server does not count it either.
  liveCount() {
    return this.listTarget.querySelectorAll(
      "[data-nested-form-target='item']:not([hidden])",
    ).length;
  }

  atLimit() {
    return (
      this.hasMaxValue && this.maxValue > 0 && this.liveCount() >= this.maxValue
    );
  }

  refreshLimit() {
    if (!this.hasMaxValue || this.maxValue <= 0) return;

    const button = this.element.querySelector(
      "[data-action~='nested-form#add']",
    );
    if (!button) return;

    const atLimit = this.atLimit();
    button.disabled = atLimit;
    this.limitNotice(button).hidden = !atLimit;
  }

  limitNotice(button) {
    if (!this.notice) {
      this.notice = document.createElement("p");
      this.notice.className = "form-text mb-0";
      this.notice.setAttribute("role", "status");
      this.notice.dataset.nestedFormLimitNotice = "";
      this.notice.textContent = `That's the most we can take here (${this.maxValue}). Tell us about any others when we contact you.`;
      button.insertAdjacentElement("afterend", this.notice);
    }
    return this.notice;
  }
}
